/**
 * Persistent "Finish setup" banner (Option 2). Shown across the marketer-view
 * shell while a user is exploring during onboarding (still needs onboarding).
 * Clicking "Finish setup" clears the explore flag and returns to the wizard.
 *
 * Styled to the marketer-view palette (amber-soft on slate) rather than the
 * default Antd Alert, so it reads as part of the shell.
 */

import React from 'react';
import { RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSurveysQuery } from '../../../services/nestjs-base-api';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { setExploringOnboarding } from './onboardingExplore';
import { THEME } from '../config/theme';

export const MarketerOnboardingBanner: React.FunctionComponent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const marketerRoot = useMarketerRoot();

  const { data: surveysData } = useGetSurveysQuery(
    {
      jwtToken: user?.jwtToken || '',
      organisationId: user?.organisation?.id || 0,
    },
    { skip: !user?.jwtToken || !user?.organisation?.id },
  );
  const hasActiveSurveys =
    !!surveysData &&
    surveysData.length > 0 &&
    surveysData.some((s: { status?: string }) => s.status === 'active');

  // Only nudge "Finish setup" until the first survey exists — after that
  // onboarding is effectively done (even if the flag lingers).
  if (!user?.needsOnboarding || hasActiveSurveys) {
    return null;
  }

  const handleFinish = () => {
    setExploringOnboarding(user.id, false);
    navigate(marketerRoot);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        padding: '10px 20px',
        background: THEME.primarySoft,
        borderBottom: `1px solid ${THEME.border}`,
        color: THEME.textBase,
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        <FormattedMessage
          id="marketerView.onboarding.bannerMessage"
          defaultMessage="Finish setting up your brand to unlock your insights."
        />
      </span>
      <button
        type="button"
        onClick={handleFinish}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: THEME.primary,
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: 13.5,
          padding: '8px 18px',
          borderRadius: 10,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        <RocketOutlined />
        <FormattedMessage
          id="marketerView.onboarding.finishSetup"
          defaultMessage="Finish setup"
        />
      </button>
    </div>
  );
};
